import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { listLLMKeys } from '../llm-keys/api';
import { listWallets } from '../wallets/api';
import { createAgent, listTemplates, type AgentTemplate } from './api';

// Generic is intentionally not surfaced — every agent in production should be
// typed, so the system prompt and toolset stay predictable. Backend keeps the
// 'generic' template seeded for advanced users via the API.
const PUBLIC_TEMPLATES = new Set([
  'payment',
  'trading',
  'compliance',
  'orchestrator',
  'treasury',
  'perpetuals',
]);

const TEMPLATE_BLURBS: Record<string, string> = {
  payment:
    'Pays vendors, processes payroll, refunds, recurring transfers. Conservative by default — only allowed recipients, capped value per tx.',
  trading: 'Executes swaps on whitelisted DEXs under strict slippage and stop-loss rules.',
  compliance:
    'Reviews a transaction for AML / sanctions / policy risk before it executes. Pulls signals from provider connectors (Chainalysis, SEON, mock, internal), writes a Drive report, and either auto-approves or routes high-risk cases to the Reviews queue for human sign-off.',
  orchestrator:
    'Reads inputs (Drive / Sheets / Slack / …), plans, and delegates execution to specialist agents via A2A. Never holds the wallet keys itself.',
  treasury:
    'Runs the organisation\'s Bit2Me Exchange account: buy / sell crypto with EUR, withdraw to allow-listed addresses. EUR-priced caps enforced server-side.',
  perpetuals:
    'Opens and manages leveraged perpetual positions on Hyperliquid. Caps on leverage, position size, total exposure and funding-rate tolerance enforced server-side. Suitable for hedging, funding capture, and bounded directional bets. Builder code customisable.',
};

export function NewAgentPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';
  const isTEEOrg = org?.signing_mode === 'tee';
  const nav = useNavigate();

  const templates = useQuery({
    queryKey: ['agent-templates', orgId],
    queryFn: () => listTemplates(orgId),
    enabled: !!orgId,
  });
  const keys = useQuery({
    queryKey: ['llm-keys', orgId],
    queryFn: () => listLLMKeys(orgId),
    enabled: !!orgId,
  });
  const wallets = useQuery({
    queryKey: ['wallets-for-agent', orgId],
    queryFn: () => listWallets(orgId, { limit: 200 }).then((p) => p.items),
    enabled: !!orgId,
  });

  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [llmKeyId, setLLMKeyId] = useState('');
  const [walletId, setWalletId] = useState('');
  const [systemPromptDraft, setSystemPromptDraft] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [error, setError] = useState<string | null>(null);

  const systemPromptEdited =
    !!selectedTemplate && systemPromptDraft.trim() !== selectedTemplate.system_prompt.trim();

  const create = useMutation({
    mutationFn: () =>
      createAgent(orgId, {
        template_slug: selectedTemplate!.slug,
        name,
        description,
        llm_key_id: llmKeyId,
        wallet_id: walletId || undefined,
        enabled_tools: selectedTemplate!.default_tools,
        // Only persist a custom prompt when the operator actually
        // changed it — otherwise we'd freeze the template and prevent
        // future Sigil updates from reaching this agent.
        custom_system_prompt: systemPromptEdited ? systemPromptDraft : undefined,
        specialization: specialization.trim() || undefined,
      }),
    onSuccess: (a) => nav(`/agents/${a.id}`),
    onError: (e: unknown) => setError(extractError(e)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!selectedTemplate || !name || !llmKeyId) {
      setError('template, name and LLM key are required');
      return;
    }
    create.mutate();
  }

  // When the operator picks a template, prefill the editable prompt with
  // the template's baseline so the textarea starts populated.
  function pickTemplate(t: AgentTemplate) {
    setSelectedTemplate(t);
    setSystemPromptDraft(t.system_prompt);
    setSpecialization('');
  }

  if (!selectedTemplate) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-ink-primary">
            What does this agent do?
          </h1>
          <p className="mt-1 text-sm text-ink-secondary">
            Choose an archetype. Sigil ships the system prompt + curated toolset; you fill in
            the wallet, LLM key, and policy bits in the next step.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {templates.data
            ?.filter((t) => PUBLIC_TEMPLATES.has(t.slug))
            .map((t) => (
              <button
                key={t.slug}
                onClick={() => pickTemplate(t)}
                className="group rounded-xl border border-line-soft bg-surface-white p-6 text-left transition-all hover:border-bit2me hover:shadow-md"
              >
                <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
                  {t.slug}
                </p>
                <h2 className="mt-2 text-lg font-semibold tracking-tightish text-ink-primary group-hover:text-bit2me">
                  {t.name}
                </h2>
                <p className="mt-3 text-sm text-ink-secondary">{TEMPLATE_BLURBS[t.slug] ?? t.description}</p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-eyebrow text-ink-tertiary">
                  {t.default_tools.length} default tools
                </p>
              </button>
            ))}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-ink-primary">
            New {selectedTemplate.name}
          </h1>
          <p className="mt-1 text-sm text-ink-secondary">{selectedTemplate.description}</p>
        </div>
        <Button variant="ghost" type="button" onClick={() => setSelectedTemplate(null)}>
          Change type
        </Button>
      </div>

      <Card>
        <CardBody className="text-xs text-ink-tertiary">
          Sigil-side auth is your session here — no <code>sk_live_</code> key needed. The only
          external credential is the LLM key, which Sigil stores encrypted and only the runner
          decrypts at execution time.
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Basics" />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Name"
            placeholder="vendor-pay-bot"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            label="Description (optional)"
            placeholder="What this agent does in your own words"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div>
            <label className="block text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">
              LLM key
            </label>
            <select
              className="mt-1 block h-10 w-full rounded-lg border border-line bg-surface-white px-3 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
              value={llmKeyId}
              onChange={(e) => setLLMKeyId(e.target.value)}
              required
            >
              <option value="">Select a key…</option>
              {keys.data?.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name} ({k.key_prefix}…)
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-tertiary">
              Don't have a key? Add one in{' '}
              <a href="/llm-keys" className="text-bit2me hover:underline">
                LLM keys
              </a>
              .
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">
              Wallet{' '}
              {['compliance', 'orchestrator', 'treasury'].includes(selectedTemplate.slug) && '(optional)'}
            </label>
            <select
              className="mt-1 block h-10 w-full rounded-lg border border-line bg-surface-white px-3 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
              value={walletId}
              onChange={(e) => setWalletId(e.target.value)}
              required={!['compliance', 'orchestrator', 'treasury'].includes(selectedTemplate.slug)}
              disabled={!isTEEOrg}
            >
              <option value="">
                {isTEEOrg ? 'Select a wallet…' : 'Org is not in TEE mode — no wallets available'}
              </option>
              {isTEEOrg &&
                walletsForTemplate(wallets.data ?? [], selectedTemplate.slug).map((w) => (
                  <option key={w.id} value={w.id}>
                    {shortAddr(w.address)} — {curveLabel(w.curve)}
                  </option>
                ))}
            </select>
            {!isTEEOrg && (
              <p className="mt-1 text-xs text-danger">
                Agents require an organisation in TEE signing mode. Yours is configured for
                client-side signing — switching mode is destructive (it invalidates existing
                wallets), so the safer path is to create a new TEE-mode organisation.
              </p>
            )}
            {isTEEOrg && (
              <p className="mt-1 text-xs text-ink-tertiary">
                Showing wallets compatible with this agent type.{' '}
                <a
                  href="https://platform.sigilkeys.com/wallets"
                  target="_blank"
                  rel="noreferrer"
                  className="text-bit2me hover:underline"
                >
                  Manage wallets →
                </a>
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="System prompt"
          description="The baseline Sigil ships for this agent type. You can edit it — but if you do you take ownership of the behaviour and will not get future Sigil updates to this prompt. Prefer the Specialization field below to narrow scope without rewriting from scratch."
        />
        <CardBody className="space-y-2">
          <textarea
            className="block w-full min-h-[14rem] max-h-[28rem] resize-y rounded-md border border-line-soft bg-surface-white p-3 font-mono text-xs leading-relaxed text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
            value={systemPromptDraft}
            onChange={(e) => setSystemPromptDraft(e.target.value)}
          />
          <div className="flex items-center justify-between gap-3 text-[11px]">
            {systemPromptEdited ? (
              <span className="text-amber-700">
                Edited — this agent will use your version, not Sigil's baseline.
              </span>
            ) : (
              <span className="text-ink-tertiary">
                Untouched — Sigil keeps this in sync with future template updates.
              </span>
            )}
            {systemPromptEdited && (
              <button
                type="button"
                onClick={() => setSystemPromptDraft(selectedTemplate.system_prompt)}
                className="text-bit2me hover:underline"
              >
                Reset to baseline
              </button>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Specialization (recommended)"
          description="What does THIS particular agent do? Example: 'Only execute USDC transfers. Reject any other asset. Operate exclusively on Polygon.' Appended to the system prompt above at every run — keeps the baseline intact and is the right place for per-agent narrowing."
        />
        <CardBody>
          <textarea
            className="block w-full min-h-[6rem] max-h-[16rem] resize-y rounded-md border border-line bg-surface-white p-3 text-sm text-ink-primary focus:border-bit2me focus:outline-none focus:ring-1 focus:ring-bit2me"
            value={specialization}
            onChange={(e) => setSpecialization(e.target.value)}
            placeholder="Only sign payments to vendors marked 'recurring'. Refuse one-off invoices — escalate to the operator instead."
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Default toolset" />
        <CardBody>
          <div className="flex flex-wrap gap-2">
            {selectedTemplate.default_tools.map((t) => (
              <span
                key={t}
                className="rounded-full border border-line bg-surface-soft/60 px-3 py-1 font-mono text-xs text-ink-secondary"
              >
                {t}
              </span>
            ))}
          </div>
        </CardBody>
      </Card>

      {error && (
        <div className="rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create agent'}
        </Button>
      </div>
    </form>
  );
}

function shortAddr(a: string): string {
  return a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-4)}` : a;
}

// Each archetype only knows how to sign certain things, so we filter the
// wallet picker to keep the operator out of trouble. Payment + Trading do
// EVM tx construction (secp256k1). Compliance is read-only so any wallet
// is acceptable as the subject of monitoring.
function walletsForTemplate(
  wallets: import('../wallets/api').WalletListItem[],
  templateSlug: string,
) {
  const active = wallets.filter((w) => w.status === 'active');
  if (templateSlug === 'payment' || templateSlug === 'trading') {
    return active.filter((w) => w.curve === 'secp256k1');
  }
  return active;
}

function curveLabel(curve: string): string {
  switch (curve) {
    case 'secp256k1':
      return 'EVM (Ethereum, Base, Arbitrum, …)';
    case 'ed25519':
      return 'Solana / Near / Aptos / Stellar';
    default:
      return curve;
  }
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { error?: { message?: string; code?: string } } } }).response;
    if (resp?.data?.error) return resp.data.error.message ?? resp.data.error.code ?? 'unknown error';
  }
  return e instanceof Error ? e.message : 'unknown error';
}
